import { DataTypeCode } from "../../../common/enums/DataTypeCode";
import { Ticket, TicketPersonFormField } from "../../../types/reservation";

export const KOK_AGE_CONDITIONAL_MAPS = new Set([
  "TypeOfCertificate",
  "CertificationNumber",
  "SportsCard",
]);

export type TicketPersonData = Record<number, Record<string, string | null | undefined>>;


// MemberCard
const MEMBER_CARD_REGEX = /^[MK]\d{8}$/;

export function isValidMemberCard(value: string): boolean {
  return MEMBER_CARD_REGEX.test(value);
}

/** Returns true when the MemberCard field should eagerly trigger validation while typing. */
export function shouldEagerValidateMemberCard(value: string): boolean {
  return value.length > 0 && (value.length > 9 || !/^[MK]/.test(value));
}

// FinancialSupportCoupon
const FINANCIAL_SUPPORT_COUPON_REGEX = /^G\d{8}$/;

export function isValidFinancialSupportCoupon(value: string): boolean {
  return FINANCIAL_SUPPORT_COUPON_REGEX.test(value);
}

/** Returns true when the FinancialSupportCoupon field should eagerly trigger validation while typing. */
export function shouldEagerValidateFinancialSupportCoupon(value: string): boolean {
  return value.length > 0 && (value.length > 9 || !/^G/.test(value));
}

// SportsCard — format: F followed by 8 digits (e.g. F12345678)
const SPORTS_CARD_REGEX = /^F\d{8}$/;

export function isValidSportsCard(value: string): boolean {
  return SPORTS_CARD_REGEX.test(value);
}

/** Returns true when the SportsCard field should eagerly trigger validation while typing. */
export function shouldEagerValidateSportsCard(value: string): boolean {
  return value.length > 0 && (value.length > 9 || !/^F/.test(value));
}

/**
 * Returns the action to take on onChange for fields with eager validation.
 * - "trigger"  → value is definitively invalid, run full validation
 * - "clear"    → value is a valid prefix, clear any existing error
 * - null       → field is not an eager-validated field, use default behaviour
 */
export function getEagerFieldAction(
  ticketPersonMap: string | null | undefined,
  value: string,
  hasError: boolean,
): "trigger" | "clear" | null {
  let shouldEager: boolean;

  if (ticketPersonMap === "MemberCard") {
    shouldEager = shouldEagerValidateMemberCard(value);
  } else if (ticketPersonMap === "FinancialSupportCoupon") {
    shouldEager = shouldEagerValidateFinancialSupportCoupon(value);
  } else if (ticketPersonMap === "SportsCard") {
    shouldEager = shouldEagerValidateSportsCard(value);
  } else {
    return null;
  }

  if (shouldEager) return "trigger";
  if (hasError) return "clear";
  return null;
}

// AMKA
export function isValidAmka(value: string): boolean {
  if (!/^\d{11}$/.test(value) || value === "00000000000") return false;
  let sum = 0;
  for (let i = 1; i <= value.length; i++) {
    let digit = parseInt(value.charAt(i - 1), 10);
    if (i % 2 === 0) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  return sum % 10 === 0;
}

// EVP-11021. Extracts the holder DOB from the leading 6 digits of a valid AMKA.
// Century resolution per spec: interpret YY as 20YY first; if that would be a
// future date relative to `today`, fall back to 19YY. Returns null on parse failure.
export function extractAmkaDob(value: string, today: Date): Date | null {
  if (!/^\d{6}/.test(value)) return null;
  const day = parseInt(value.substring(0, 2), 10);
  const month = parseInt(value.substring(2, 4), 10);
  const yearTwoDigits = parseInt(value.substring(4, 6), 10);
  if (!day || !month || month < 1 || month > 12) return null;

  const build = (fullYear: number): Date | null => {
    const d = new Date(fullYear, month - 1, day);
    if (
      d.getFullYear() !== fullYear ||
      d.getMonth() !== month - 1 ||
      d.getDate() !== day
    ) {
      return null;
    }
    return d;
  };

  const twentyHundreds = build(2000 + yearTwoDigits);
  if (twentyHundreds && twentyHundreds.getTime() <= today.getTime()) {
    return twentyHundreds;
  }
  return build(1900 + yearTwoDigits);
}

// True while the holder is still under `ageCap` — up to the day before their
// `ageCap`-th birthday.
export function isAmkaHolderUnderAge(
  amka: string,
  today: Date,
  ageCap: number,
): boolean {
  const dob = extractAmkaDob(amka, today);
  if (!dob) return false;
  const capReachedOn = new Date(
    dob.getFullYear() + ageCap,
    dob.getMonth(),
    dob.getDate(),
  );
  return today.getTime() < capReachedOn.getTime();
}

// Olympiacos only: zero-price and kids-discount tickets do not have to present a
// fancard, so the SportsCard field is hidden and its requiredness waived. Mirrors
// the per-ticket waiver in OlympiacosTicketHolderValidator on the server.
export function isOlympiacosFancardWaived(
  ticket: Pick<Ticket, "price" | "discount">,
  isOlympiacosEvent: boolean,
): boolean {
  if (!isOlympiacosEvent) return false;

  return ticket.price === 0 || !!ticket.discount?.isKidsTicket;
}

// KOK Validation
export function getKokAgeFieldId(
  ticketPersonFormFields: TicketPersonFormField[],
  isKokEvent: boolean,
): string | undefined {
  if (!isKokEvent) return undefined;
  return ticketPersonFormFields.find((f) => f.ticketPersonMap === "Age")?.dynamicAttributeId;
}

export function isKokAgeEligible(
  ticketId: number | string,
  ticketPersonData: TicketPersonData,
  kokAgeFieldId: string | undefined,
): boolean {
  if (!kokAgeFieldId) return false;
  const rawAge = ticketPersonData[ticketId]?.[kokAgeFieldId];
  const age = rawAge ? parseInt(rawAge, 10) : NaN;
  return !isNaN(age) && age >= 14;
}

// Returns true when all required fields for a single ticket are filled,
// respecting the KOK age-gate (conditional fields are skipped when age < 14) and
// the Olympiacos zero-price / kids-ticket fancard waiver.
// Pass kokAgeFieldId (from getKokAgeFieldId) — undefined means not a KOK event.
export function isTicketPersonComplete(
  ticket: Ticket,
  ticketPersonFormFields: TicketPersonFormField[],
  ticketPersonData: TicketPersonData,
  isKokEvent?: boolean,
  kokAgeFieldId?: string | undefined,
  isOlympiacosEvent?: boolean,
): boolean {
  const ticketId = ticket.id;
  const ticketPrice = ticket.price;
  const ageEligible = kokAgeFieldId
    ? isKokAgeEligible(ticketId, ticketPersonData, kokAgeFieldId)
    : true;

  const fancardWaived = isOlympiacosFancardWaived(ticket, !!isOlympiacosEvent);

  for (const field of ticketPersonFormFields) {
    if (!field.required) continue;
    if (field.dataTypeCode === DataTypeCode.warningMessage) continue;
    if (fancardWaived && field.ticketPersonMap === "SportsCard") continue;
    if (field.notRequiredForFreeTickets && ticketPrice === 0) continue;
    if (
      isKokEvent &&
      field.ticketPersonMap &&
      KOK_AGE_CONDITIONAL_MAPS.has(field.ticketPersonMap) &&
      !ageEligible
    )
      continue;

    const value = ticketPersonData[ticketId]?.[field.dynamicAttributeId];
    if (field.dataTypeCode === DataTypeCode.checkbox) {
      if (value !== "true") return false;
    } else {
      if (!value) return false;
    }
  }

  return true;
}
